import BlockLayout from '../utils/BlockLayout';

/**
 * this file contains all the method related to models, for example, import/delete etc
 * all the public api should take clara api as the first argument
 * and return the method function
 */

/**
 * public api
 */
const importModel = api => {
  const fetchedScene = {};
  return async (sceneId, options) => {
    const { blocksData, parent, transform = {} } = options;
    const { translation, rotation } = transform;
    if (!fetchedScene[sceneId]) {
      await api.sceneIO.fetch(sceneId, null, { waitForPublish: true });
      fetchedScene[sceneId] = true;
    }

    if (blocksData) {
      Object.keys(blocksData).forEach(blockName => {
        if (!api.scene.find({ from: sceneId, name: blockName }))
          throw new Error(
            `Scene ${sceneId} missing block reference ${blockName}`
          );
      });
    }

    const modelId = await api.scene.cloneModel({ sceneId });
    if (parent) api.sceneGraph.reparent(parent, [modelId]);
    if (translation)
      api.scene.set(
        { id: modelId, plug: 'Transform', property: 'translation' },
        translation
      );
    if (rotation)
      api.scene.set(
        { id: modelId, plug: 'Transform', property: 'rotation' },
        rotation
      );

    await api.sceneIO.whenLoaded();
    api.player.frameScene();

    return {
      modelId,
      sceneId,
      blocks: initBlockLayouts(api, modelId, blocksData),
    };
  };
};

function initBlockLayouts(api, modelId, blocks) {
  if (!blocks) return;
  return Object.entries(blocks).reduce((initBlocks, [blockName, block]) => {
    const referenceTransform = api.scene.getWorldTransform(
      api.scene.find({ from: modelId, name: blockName })
    );
    const modelTransform = api.scene.getWorldTransform(modelId);
    initBlocks[blockName] = new BlockLayout(
      api.deps.THREE,
      referenceTransform,
      modelTransform,
      block.rowHeights,
      block.colWidths,
      {
        occupyRow: block.occupyRow,
        occupyCol: block.occupyCol,
        withRotation: block.withRotation,
        marginTop: block.marginTop,
        marginBottom: block.marginBottom,
        marginLeft: block.marginLeft,
        marginRight: block.marginRight,
      }
    );
    return initBlocks;
  }, {});
}

/**
 * public api
 */
const deleteModel = api => (models, modelId) => {
  const newModels = { ...models };
  const parent = newModels[modelId].parent;
  if (parent) {
    const newChildren = newModels[parent].children.slice();
    newChildren.splice(newChildren.indexOf(modelId), 1);
    Object.assign(newModels, {
      [parent]: { ...newModels[parent], children: newChildren },
    });
  }
  removeModelRecursively(newModels, modelId);
  api.sceneGraph.deleteNode(modelId);
  return newModels;
};

function removeModelRecursively(models, modelId, optionalDeleteModel) {
  for (let idx = 0; idx < models[modelId].children.length; ++idx)
    removeModelRecursively(models, models[modelId].children[idx]);
  if (optionalDeleteModel) optionalDeleteModel[modelId] = models[modelId];
  delete models[modelId];
}

const validateModels = () => (configuration, models) => {
  const itemFromConfig = countItemFromConfiguration(configuration);
  let itemFromModels = Object.keys(models).length;
  if (itemFromConfig === itemFromModels) return;
  const newModels = { ...models };
  const deletedModels = {};
  while (itemFromModels > itemFromConfig) {
    Object.assign(
      deletedModels,
      filterModelsFromConfiguration(configuration, newModels)
    );
    itemFromModels = Object.keys(newModels).length;
  }
  if (itemFromModels === itemFromConfig) {
    // Todo: send deletedModels to rollbar
    console.log(deletedModels);
    //.....
    return newModels;
  } else {
    // data can't be fixed
    // Todo: This case shouldn't really happen, althought we should handle the error properly
    throw new Error('Order data can not be fixed!');
  }
};

function countItemFromConfiguration(configuration) {
  return Object.values(configuration).reduce(
    (count, configValue) =>
      count +
      (configValue.value
        ? configValue.modelId
          ? 1
          : 0
        : Object.values(configValue).reduce(
            (subCount, itemList) => subCount + itemList.length,
            0
          )),
    0
  );
}

function filterModelsFromConfiguration(configuration, models) {
  let deletedModels;
  Object.entries(models).forEach(([modelId, { attribute, value }]) => {
    if (deletedModels && deletedModels[modelId]) return;
    let invalidModel = false;

    try {
      invalidModel = configuration[attribute].value
        ? configuration[attribute].modelId !== modelId
        : !configuration[attribute][value].find(
            item => item.modelId === modelId
          );
    } catch (e) {
      invalidModel = true;
    }
    if (invalidModel) {
      if (!deletedModels) deletedModels = {};
      removeModelRecursively(models, modelId, deletedModels);
    }
  });
  return deletedModels;
}

/**
 * public api
 */
const selectModel = (api, highligher) => modelId => {
  api.selection.selectNode(modelId);
  highlighingModel(api, highligher, modelId);
};

function highlighingModel(api, highligher, modelId) {
  highligher.selectionSet.clear();
  highligher.selectionSet.add(findModelDirectChildren(api, modelId));
}

// due to the reparent, a model will have its own children mesh as well as other models
// this function will only rethrn the model's directly child mesh
function findModelDirectChildren(api, modelId) {
  function findModelDirectChildrenRecursively(api, id, result) {
    const validChildren = api.scene.filter({
      from: id,
      type: ['PolyMesh', 'BinMesh', 'Null'],
      shallow: true,
    });
    result.push(id);
    for (let idx = 0; idx < validChildren.length; ++idx)
      findModelDirectChildrenRecursively(api, validChildren[idx], result);
  }
  const children = [];
  findModelDirectChildrenRecursively(api, modelId, children);
  return children;
}

/**
 * public
 */
const deselectModel = (api, highligher) => () => {
  api.selection.deselectAll();
  highligher.selectionSet.clear();
};

/**
 * public api
 */

const findModel = api => nodeId => {
  let id = nodeId;
  while (id && api.scene.get({ id, property: 'type' }) !== 'Model') {
    id = api.scene.find({ id, parent: true });
  }
  return id;
};

/**
 * public api
 */

const updateBlocks = api => {
  const callback = models =>
    models.forEach(model =>
      api.scene.set(
        { id: model.modelId, plug: 'Transform', property: 'translation' },
        model.translation
      )
    );
  return (modelId, blocks) => {
    if (!blocks) return;
    const modelTransform = api.scene.getWorldTransform(modelId);
    Object.entries(blocks).forEach(([name, block]) => {
      const referenceTransform = api.scene.getWorldTransform(
        api.scene.find({ from: modelId, name })
      );
      block.updateTransform(referenceTransform, modelTransform, callback);
    });
  };
};

const modelApiCreater = (api, highligher) => ({
  importModel: importModel(api),
  deleteModel: deleteModel(api),
  selectModel: selectModel(api, highligher),
  deselectModel: deselectModel(api, highligher),
  findModel: findModel(api),
  updateBlocks: updateBlocks(api),
  validateModels: validateModels(),
});

export default modelApiCreater;



// WEBPACK FOOTER //
// ./src/modules/model.js